import React, { useState, useEffect } from 'react';
import { Customer, CustomerType, PricingTier, CustomerCreate, CustomerUpdate } from '../../services/customerService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getTierLabel, getTierDescription } from '../../utils/pricingTier';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CustomerCreate | CustomerUpdate) => Promise<void>;
  onCancel: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ customer, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<Partial<CustomerCreate>>({
    name: '',
    phone: '',
    email: '',
    address: '',
    type: CustomerType.END_USER,
    pricing_tier: PricingTier.NO_DISCOUNT,
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const currentUser = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name,
        phone: customer.phone,
        email: customer.email || '',
        address: customer.address || '',
        type: customer.type,
        pricing_tier: customer.pricing_tier || PricingTier.NO_DISCOUNT,
        notes: customer.notes || '',
      });
    }
  }, [customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (customer) {
        // Update - no branch_id needed
        await onSubmit(formData);
      } else {
        // Create - need branch_id
        if (!currentUser?.branch_id) {
          alert('Branch ID tidak ditemukan');
          return;
        }
        await onSubmit({
          ...formData,
          branch_id: currentUser.branch_id,
        } as CustomerCreate);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CustomerCreate, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nama Customer *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Contoh: Toko Berkah HP"
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          No. Telepon *
        </label>
        <input
          type="text"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="081234567890"
        />
        <p className="text-xs text-gray-500 mt-1">Nomor telepon harus unik</p>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email (Opsional)
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="email@example.com"
        />
      </div>

      {/* Customer Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tipe Customer *
        </label>
        <select
          value={formData.type}
          onChange={(e) => handleChange('type', e.target.value as CustomerType)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={CustomerType.END_USER}>End User (Tanpa Garansi)</option>
          <option value={CustomerType.TECHNICIAN}>Teknisi/Reseller (Garansi 30 Hari)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {formData.type === CustomerType.TECHNICIAN
            ? '⚠️ Teknisi mendapat garansi 30 hari untuk barang sensitif'
            : 'ℹ️ End user tidak mendapat garansi otomatis'}
        </p>
      </div>

      {/* Pricing Tier */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pricing Tier *
        </label>
        <select
          value={formData.pricing_tier}
          onChange={(e) => handleChange('pricing_tier', e.target.value as PricingTier)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={PricingTier.NO_DISCOUNT}>Grade 1: No Discount (0%)</option>
          <option value={PricingTier.BRONZE}>Grade 2: Bronze - 3% Discount</option>
          <option value={PricingTier.SILVER}>Grade 3: Silver - 5% Discount</option>
          <option value={PricingTier.GOLD}>Grade 4: Gold - 10% Cash / 5% Tempo</option>
          <option value={PricingTier.RETAIL_MARKUP}>Grade 5: Retail Markup +35%</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {formData.pricing_tier && getTierDescription(formData.pricing_tier)}
        </p>
        {formData.pricing_tier === PricingTier.GOLD && (
          <p className="text-xs text-amber-600 mt-1">
            ⚠️ Diskon tempo 5% hanya berlaku untuk pembayaran tempo maksimal 7 hari
          </p>
        )}
        {formData.pricing_tier === PricingTier.RETAIL_MARKUP && (
          <p className="text-xs text-purple-600 mt-1">
            ℹ️ Harga akan dinaikkan 35% dari harga base (markup, bukan diskon)
          </p>
        )}
      </div>

      {/* 
      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Alamat (Opsional)
        </label>
        <textarea
          value={formData.address}
          onChange={(e) => handleChange('address', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Alamat lengkap customer"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Catatan (Opsional)
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Catatan tambahan..."
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Batal
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
        >
          {loading ? 'Menyimpan...' : customer ? 'Update Customer' : 'Simpan Customer'}
        </button>
      </div>
    </form>
  );
};

export default CustomerForm;
